import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

// Pages
import CompanyOverview from '../pages/CompanyOverview';
import Products from '../pages/Products';
import DeliveryPerformance from '../pages/DeliveryPerformance';
import CustomerSupport from '../pages/CustomerSupport';
import Certification from '../pages/Certification';
import CertificationManagement from '../pages/CertificationManagement';
import VideoGallery from '../pages/VideoGallery';
import EProcurement from '../pages/EProcurement';
import EnEProcurement from '../pages/en/EProcurement';
import Admin from '../pages/Admin';
import ESG from '../pages/ESG';

// English Pages
import EnCompanyOverview from '../pages/en/CompanyOverview';
import EnProducts from '../pages/en/Products';
import EnDeliveryPerformance from '../pages/en/DeliveryPerformance';
import EnCustomerSupport from '../pages/en/CustomerSupport';
import EnCertification from '../pages/en/Certification';
import EnCertificationManagement from '../pages/en/CertificationManagement';
import EnESG from '../pages/en/ESG';

// Components
import Header from './Header';
import Hero from './Hero';
import Partners from './Partners';
import WhyChooseUs from './WhyChooseUs';
import VideoSection from './VideoSection';
import MainServices from './MainServices';
import CustomSolution from './CustomSolution';
import Footer from './Footer';

// English Components
import EnHeader from './en/Header';
import EnHero from './en/Hero';
import EnPartners from './en/Partners';
import EnMainServices from './en/MainServices';
import EnWhyChooseUs from './en/WhyChooseUs';
import EnVideoSection from './en/VideoSection';
import EnCustomSolution from './en/CustomSolution';
import EnFooter from './en/Footer';

const LanguageRouter = () => {
  const { language, setLanguage } = useLanguage();
  const location = useLocation();

  // URL에서 언어 코드 추출
  const pathSegments = location.pathname.split('/');
  const urlLanguage = pathSegments[1]; // /ko/ 또는 /en/

  // 언어에 따라 body에 속성 설정
  useEffect(() => {
    document.body.setAttribute('data-language', urlLanguage || language);
  }, [urlLanguage, language]);

  // URL 언어에 맞게 언어 상태 동기화
  useEffect(() => {
    if (urlLanguage === 'ko' || urlLanguage === 'en') {
      if (urlLanguage !== language) {
        // URL 언어로 상태 변경 (리렌더링만, 리다이렉트 없음)
        setLanguage(urlLanguage);
      }
    } else {
      // 언어 코드가 없는 경우 기본 언어로 리다이렉트
      if (location.pathname !== `/${language}` && location.pathname !== '/') {
        window.location.href = `/${language}${location.pathname}`;
      }
    }
  }, [urlLanguage, language, location.pathname, setLanguage]);

  // 언어별 컴포넌트 렌더링
  const renderMainPage = () => {
    if (language === 'en') {
      return (
        <>
          <EnHero />
          <EnPartners />
          <EnMainServices />
          <EnWhyChooseUs />
          <EnVideoSection />
          <EnCustomSolution />
        </>
      );
    } else {
      return (
        <>
          <Hero />
          <Partners />
          <MainServices />
          <WhyChooseUs />
          <VideoSection />
          <CustomSolution />
        </>
      );
    }
  };

  return (
    <>
      {/* 언어별 헤더 */}
      {language === 'en' ? <EnHeader /> : <Header />}
      
      <Routes>
        {/* 메인 페이지 */}
        <Route path={`/${language}`} element={renderMainPage()} />
        
        {/* 서브 페이지들 */}
        <Route path={`/${language}/company-overview`} element={
          language === 'en' ? <EnCompanyOverview /> : <CompanyOverview />
        } />
        <Route path={`/${language}/products`} element={
          language === 'en' ? <EnProducts /> : <Products />
        } />
        <Route path={`/${language}/delivery-performance`} element={
          language === 'en' ? <EnDeliveryPerformance /> : <DeliveryPerformance />
        } />
        <Route path={`/${language}/customer-support`} element={
          language === 'en' ? <EnCustomerSupport /> : <CustomerSupport />
        } />
      <Route path={`/${language}/certification-detail`} element={
        language === 'en' ? <EnCertification /> : <Certification />
      } />
      <Route path={`/${language}/certification-management`} element={
        language === 'en' ? <EnCertificationManagement /> : <CertificationManagement />
      } />
        <Route path={`/${language}/video-gallery`} element={<VideoGallery />} />
        <Route path={`/${language}/e-procurement`} element={
          language === 'en' ? <EnEProcurement /> : <EProcurement />
        } />
        <Route path={`/${language}/manage`} element={<Admin />} />
        <Route path={`/${language}/esg`} element={
        language === 'en' ? <EnESG /> : <ESG />
      } />
        
        {/* 루트 경로는 기본 언어로 리다이렉트 */}
        <Route path="/" element={<Navigate to={`/${language}`} replace />} />
        
        {/* 언어 코드가 없는 기존 경로들은 기본 언어로 리다이렉트 */}
        <Route path="/company-overview" element={<Navigate to={`/${language}/company-overview`} replace />} />
        <Route path="/products" element={<Navigate to={`/${language}/products`} replace />} />
        <Route path="/delivery-performance" element={<Navigate to={`/${language}/delivery-performance`} replace />} />
        <Route path="/customer-support" element={<Navigate to={`/${language}/customer-support`} replace />} />
        <Route path="/certification-detail" element={<Navigate to={`/${language}/certification-detail`} replace />} />
        <Route path="/certification-management" element={<Navigate to={`/${language}/certification-management`} replace />} />
        <Route path="/video-gallery" element={<Navigate to={`/${language}/video-gallery`} replace />} />
        <Route path="/e-procurement" element={<Navigate to={`/${language}/e-procurement`} replace />} />
        <Route path="/manage" element={<Navigate to={`/${language}/manage`} replace />} />
        <Route path="/esg" element={<Navigate to={`/${language}/esg`} replace />} />
      </Routes>
      
      {/* 언어별 푸터 */}
      {language === 'en' ? <EnFooter /> : <Footer />}
    </>
  );
};

export default LanguageRouter;
